import { useState, useEffect } from 'react'
import { PageHeader, Space, Button, Modal, message, notification } from 'antd'
import { EyeOutlined, UnorderedListOutlined } from '@ant-design/icons'
import { useLocation } from 'react-router-dom'
import { useQueryClient, useMutation } from 'react-query'
import useSearchParams from '../../hooks/useQuery'
import useIsMobile from '../../hooks/useIsMobile'

import { getClients, resetPassword } from '../../network/Clients'
import { getAllTags } from '../../network/ClientTags'
import { getAllInsights } from '../../network/ClientInsights'
import TableBlock from '../../components/tables/TableBlock'
import { useAuth } from '../../hooks/useAuth'
import { ROLE_PERMISSIONS } from '../../constants/common'
import {
  createUrlWithFiltersFromKeyRequest,
  createUrlWithSeparatedFilters,
} from '../../utils/filterUtils'

const getBaseUrl = (path) => {
  let p = path.split('/')
  p.pop()
  return p.join('/')
}

const Clients = ({ history }) => {
  const queryClient = useQueryClient()
  queryClient.invalidateQueries('getClientDetails')
  let location = useLocation()
  const params = useSearchParams()
  const isMobile = useIsMobile(769)
  const [selected, setSelected] = useState(null)
  const [clientTags, setClientTags] = useState([])
  const [clientInsights, setClientInsights] = useState([])
  const isRegular = location.pathname === '/clients'
  const { user } = useAuth()
  const permissions = ROLE_PERMISSIONS[user.role]
    ? ROLE_PERMISSIONS[user.role]
    : []
  const storeOptions = user.stores.map((store) => ({
    label: store.name,
    key: store.store,
  }))

  useEffect(() => {
    getAllTags({ queryKey: ['getClientTag', {}] }).then((res) => {
      if (res && res.list) {
        setClientTags(res.list)
      }
    })

    getAllInsights({ queryKey: ['getClientInsights', {}] }).then((res) => {
      if (res && res.list) {
        setClientInsights(res.list)
      }
    })
  }, [])

  let filters = (history) => ({
    fields: [
      {
        type: 'text',
        label: 'Client name',
        key: 'name',
        noRegex: true,
      },
      {
        type: 'text',
        label: 'Client ID',
        key: 'clientID',
      },
      ...(!user.franchiseStore
        ? [
            {
              type: 'text',
              label: 'Wechat ID',
              key: 'wechatID',
            },
          ]
        : []),
      {
        type: 'text',
        label: 'Phone number',
        key: 'phone',
      },
      {
        type: 'select',
        label: 'Store',
        key: 'store',
        options: storeOptions,
      },
      {
        type: 'select',
        label: 'Client Tags',
        key: 'clientTags',
        options: clientTags.map((tag) => ({
          label: tag.label,
          key: tag.id,
        })),
      },
      {
        type: 'select',
        label: 'Client Insights',
        key: 'clientInsights',
        options: clientInsights.map((insight) => ({
          label: insight.label,
          key: insight.id,
        })),
      },
    ],
    actions: [
      {
        label: '+ New client',
        isActive: permissions.includes('new_clients'),
        action: (keyRequest) => {
          const url = createUrlWithFiltersFromKeyRequest(
            `/clients/create/${user.currentStore}`,
            keyRequest
          )
          history.push(url)
        },
      },
    ],
  })

  const filtersOrders = (
    history,
    selectedRowKeys,
    isLoading,
    selectedRow,
    lll,
    location
  ) => ({
    fields: [
      {
        type: 'text',
        label: 'Client name',
        key: 'name',
        noRegex: true,
      },
      ...(!isMobile
        ? [
            {
              type: 'text',
              label: 'Client ID',
              key: 'clientID',
            },
            ...(user.franchiseStore === false
              ? [
                  {
                    type: 'text',
                    label: 'Wechat ID',
                    key: 'wechatID',
                  },
                ]
              : []),
          ]
        : []),
      {
        type: 'text',
        label: 'Phone number',
        key: 'phone',
      },
      {
        type: 'select',
        label: 'Store',
        key: 'store',
        options: storeOptions,
      },
      {
        type: 'select',
        label: 'Client Tags',
        key: 'clientTags',
        options: clientTags.map((tag) => ({
          label: tag.label,
          key: tag.id,
        })),
      },
      {
        type: 'select',
        label: 'Client Insights',
        key: 'clientInsights',
        options: clientInsights.map((insight) => ({
          label: insight.label,
          key: insight.id,
        })),
      },
    ],
    actions: [
      {
        label: 'Previous',
        second: true,
        isActive: true,
        action: () => history.push(getBaseUrl(location.pathname)),
      },
      {
        label: 'Next',
        isActive: !!selectedRow,
        action: (keyRequest) => {
          let currentPage = null
          const filterValues = params.get('filterValues')
          if (filterValues) {
            try {
              const allFilters = JSON.parse(decodeURIComponent(filterValues))
              if (allFilters._page) {
                currentPage = parseInt(allFilters._page, 10)
              }
            } catch (e) {}
          }
          if (!currentPage) {
            const pageParam = params.get('page')
            if (pageParam) {
              currentPage = parseInt(pageParam, 10)
            }
          }

          const baseUrl = `${getBaseUrl(
            location.pathname
          )}/${selectedRow}/store/${user.currentStore}`
          const url = createUrlWithFiltersFromKeyRequest(
            baseUrl,
            keyRequest,
            currentPage
          )
          history.push(url)
        },
      },
    ],
  })

  const columns = (history, keyRequest) => {
    const franchiseStore = user.franchiseStore
    return [
      {
        title: 'Client Name',
        render: (e) => `${e.firstName} ${e.lastName}`,
        key: 'name',
        sorter: true,
      },
      {
        title: 'Client ID',
        dataIndex: 'clientID',
        key: 'clientID',
        sorter: true,
      },
      ...(franchiseStore
        ? []
        : [
            {
              title: 'Wechat ID',
              dataIndex: 'wechatID',
              key: 'wechatID',
              sorter: true,
            },
          ]),
      {
        title: 'Phone Number',
        dataIndex: 'phone',
        key: 'phone',
        sorter: true,
      },
      {
        title: '',
        key: 'action',
        fixed: 'right',
        width: 50,
        render: (e) => {
          return (
            <Space>
              <Button
                type="primary"
                shape="circle"
                icon={<EyeOutlined />}
                onClick={() => {
                  let currentPage = null
                  const filterValues = params.get('filterValues')
                  if (filterValues) {
                    try {
                      const allFilters = JSON.parse(
                        decodeURIComponent(filterValues)
                      )
                      if (allFilters._page) {
                        currentPage = parseInt(allFilters._page, 10)
                      }
                    } catch (e) {}
                  }
                  if (!currentPage) {
                    const pageParam = params.get('page')
                    if (pageParam) {
                      currentPage = parseInt(pageParam, 10)
                    }
                  }

                  const url = createUrlWithSeparatedFilters(
                    `/clients/${e.id}/view`,
                    keyRequest,
                    currentPage
                  )
                  history.push(url)
                }}
              />
              <Button
                type="primary"
                shape="circle"
                icon={<UnorderedListOutlined />}
                onClick={() => {
                  let currentPage = null
                  const filterValues = params.get('filterValues')
                  if (filterValues) {
                    try {
                      const allFilters = JSON.parse(
                        decodeURIComponent(filterValues)
                      )
                      if (allFilters._page) {
                        currentPage = parseInt(allFilters._page, 10)
                      }
                    } catch (e) {}
                  }
                  if (!currentPage) {
                    const pageParam = params.get('page')
                    if (pageParam) {
                      currentPage = parseInt(pageParam, 10)
                    }
                  }

                  const baseUrl = `/clients/${e.id}/view`
                  const urlWithFilters = createUrlWithSeparatedFilters(
                    baseUrl,
                    keyRequest,
                    currentPage
                  )
                  const url = urlWithFilters.includes('?')
                    ? `${urlWithFilters}&toOrder=true`
                    : `${urlWithFilters}?toOrder=true`
                  history.push({
                    pathname: url,
                    toOrder: true,
                  })
                }}
              />
              {permissions.includes('edit_clients') && (
                <Button
                  onClick={() => {
                    const url = createUrlWithFiltersFromKeyRequest(
                      `/clients/${e.id}`,
                      keyRequest
                    )
                    history.push(url)
                  }}
                  type="primary"
                >
                  Edit client
                </Button>
              )}
              {permissions.includes('new_orders') && (
                <Button
                  onClick={() => {
                    const userId = e.id
                    const url = createUrlWithFiltersFromKeyRequest(
                      `/orders/create/${userId}/store/${user.currentStore}`,
                      keyRequest
                    )
                    history.push(url)
                  }}
                  type="primary"
                >
                  + New order
                </Button>
              )}
              {permissions.includes('new_clients') && (
                <ModalBtn
                  id={e.id}
                  keyRequest={keyRequest}
                  buttonLabel="Reset password"
                  title="Reset password"
                  content="Do you want to reset password?"
                  request={resetPassword}
                />
              )}
            </Space>
          )
        },
      },
    ]
  }

  const columnsOrders = (history, keyRequest, selectedRow) => {
    const franchiseStore = user.franchiseStore
    return [
      {
        title: 'Client Name',
        render: (e) => (
          <div className={e.id === selectedRow ? 'selected' : ''}>
            {`${e.firstName} ${e.lastName}`}
          </div>
        ),
        key: 'name',
        sorter: true,
      },
      ...(!isMobile
        ? [
            {
              title: 'Client ID',
              render: (e) => (
                <div className={e.id === selectedRow ? 'selected' : ''}>
                  {e.clientID}
                </div>
              ),
              key: 'clientID',
              sorter: true,
            },
            ...(franchiseStore
              ? []
              : [
                  {
                    title: 'Wechat ID',
                    render: (e) => (
                      <div className={e.id === selectedRow ? 'selected' : ''}>
                        {e.wechatID}
                      </div>
                    ),
                    key: 'wechatID',
                    sorter: true,
                  },
                ]),
          ]
        : []),
      {
        title: 'Phone Number',
        render: (e) => (
          <div className={e.id === selectedRow ? 'selected' : ''}>
            {e.phone}
          </div>
        ),
        key: 'phone',
        sorter: true,
      },
    ]
  }

  // getBaseUrl(location.pathname)
  return (
    <>
      <PageHeader
        className="site-page-header"
        title={
          isRegular
            ? 'Clients'
            : `Create ${
                location.pathname.includes('orders') ? 'order' : 'adjustment'
              }`
        }
        onBack={
          isRegular ? null : () => history.push(getBaseUrl(location.pathname))
        }
      />
      <TableBlock
        className={isRegular ? '' : 'has-selection order-create-client-select'}
        columns={isRegular ? columns : columnsOrders}
        filters={isRegular ? filters : filtersOrders}
        onRow={(record) => ({
          onClick: () =>
            setSelected((prev) => (prev === record.id ? null : record.id)),
        })}
        name={'getClients'}
        request={getClients}
        pageId={selected}
        otherFilters={
          user.franchiseStore || !permissions.includes('can-see-franchises')
            ? { store: user.currentStore }
            : {}
        } // Filter the client if the store is franchised
      />
      {!isRegular && (
        <Space
          style={{
            backgroundColor: '#fff',
            width: '100%',
            padding: '0px 24px 24px 24px',
          }}
        >
          <Button onClick={() => history.push(getBaseUrl(location.pathname))}>
            Previous
          </Button>
          <Button
            onClick={() =>
              history.push(
                `${getBaseUrl(location.pathname)}/${selected}/store/${
                  user.currentStore
                }`
              )
            }
            disabled={!selected}
            type="primary"
          >
            Next
          </Button>
        </Space>
      )}
    </>
  )
}

export default Clients

const ModalBtn = ({ id, buttonLabel, keyRequest, request, title, content }) => {
  const [visible, setVisible] = useState(false)
  const queryClient = useQueryClient()

  const mutation = useMutation(() => request({}, id), {
    onSuccess: (data) => {
      queryClient.setQueryData(keyRequest, (oldData) => {
        notification.info({
          message: 'User Updated',
          description: `New password is ${data.password}`,
          duration: 0,
        })

        return oldData
      })
      setVisible(false)
    },
    onError: () => {
      message.error('An error occurs')
      setVisible(false)
    },
  })

  const showModal = () => {
    setVisible(true)
  }

  const handleCancel = () => {
    setVisible(false)
  }

  return (
    <>
      <Button type="primary" onClick={showModal}>
        {buttonLabel}
      </Button>
      <Modal
        title={title}
        centered
        open={visible}
        onOk={mutation.mutate}
        onCancel={handleCancel}
        okText="Yes"
        cancelText="No"
      >
        {content}
      </Modal>
    </>
  )
}
